import React, { useEffect, useMemo } from "react";
import { Redirect, useLocation } from "react-router-dom";

import { isSignedIn } from "@culturehq/client";
import { Loader } from "@culturehq/components";

import { useProfile } from "app/context";
import { errorService, isPlatformSubdomain, seo, t } from "app/utils";

import Feed from "./Feed";
import LandingPageCallout from "./callouts/LandingPageCallout";

const getRoute = (match, fallback) => home => {
  const route = home === match ? "/" : fallback;

  return [route, (_match, location) => [route, fallback].includes(location.pathname)];
};

const getFeedRoute = getRoute("feed", "/feed");
export const useFeedRoute = profile => useMemo(
  () => getFeedRoute(profile.organization.home),
  [profile.organization.home]
);

const useHomeComponent = () => useMemo(
  () => {
    const subdomain = window.location.host.split(".")[0];
    if ((!isPlatformSubdomain(subdomain) && process.env.NODE_ENV === "production")
      || (subdomain !== "localhost:4000" && process.env.NODE_ENV !== "production")) {
      return LandingPageCallout;
    }

    return Feed;
  }, []
);

const Home = () => {
  const { profile } = useProfile();
  const location = useLocation();
  const subdomain = window.location.host.split(".")[0];
  let orgName = subdomain;
  if (subdomain.includes("-staging")) {
    [orgName] = subdomain.split("-staging");
  }

  const Component = useHomeComponent(profile);

  useEffect(
    () => {
      if (isPlatformSubdomain(subdomain)) {
        seo({
          title: t("Home - CultureHQ.com")
        });
      }
    },
    [subdomain]
  );

  const getRedirect = () => {
    if (!isSignedIn()) {
      errorService.log("Redirect to login, home");
      return "/login";
    }
    if (!profile.claimed) {
      return "/accept/sso";
    }
    return "/403";
  };

  // Wait for profile to load. Show a loading indicator until we know that we
  // have a profile in memory to check
  if (isSignedIn() && !profile.id) {
    return <Loader loading />;
  }

  if (!isSignedIn()
    && ((isPlatformSubdomain(subdomain) && process.env.NODE_ENV === "production")
      || (subdomain === "localhost:4000" && process.env.NODE_ENV !== "production"))) {
    return <Redirect to={{ pathname: getRedirect(), state: { from: location } }} />;
  }

  return <Component orgName={orgName} />;
};

export default Home;
